import { sanitizeCenterTextInput } from "./centerTextRules";
import {
  DEFAULT_LOGO_TRANSFORM,
  defaultConfiguratorState,
  type ConfiguratorState,
  type DesignMode,
  type FrameShape,
  type LightStyle,
} from "./types";

export type CpbCenterRgbRasterMode = "opaque-mask" | "full-bounds";

/**
 * IM-003: frozen CPB -> 3D payload for the center design.
 * This object should be provided by CPB as the source of truth for center content.
 */
export type CpbCenterDesignPayload = {
  mode: DesignMode;
  text: string;
  textColor: string;
  textRgb: boolean;
  logo: {
    url: string | null;
    mimeType: string | null;
    rgb: boolean;
    rgbRasterMode: CpbCenterRgbRasterMode;
  };
  /**
   * Bump when CPB updates image bytes, including stable URL cases.
   * Example: timestamp/hash/revision integer.
   */
  assetRevision: string;
};

type CpbDefaults = Partial<{
  shape: FrameShape;
  frameColor: string;
  size: number;
  ledColor: string;
  lightStyle: LightStyle;
  rgb: boolean;
  depthLayers: number;
  brightness: number;
}>;

declare global {
  interface Window {
    __CPB_DEFAULTS__?: CpbDefaults;
    __CPB_CENTER_DESIGN__?: Partial<CpbCenterDesignPayload>;
  }
}

const SHAPES: FrameShape[] = ["square", "circle", "rectangle", "vrect", "hoval", "voval"];
const LIGHT_STYLES: LightStyle[] = ["bar", "text", "point"];
const DESIGN_MODES: DesignMode[] = ["nothing", "text", "logo"];
const RASTER_MODES: CpbCenterRgbRasterMode[] = ["opaque-mask", "full-bounds"];

function pickShape(raw: string | null | undefined): FrameShape | undefined {
  if (!raw) return undefined;
  return SHAPES.includes(raw as FrameShape) ? (raw as FrameShape) : undefined;
}

function pickLightStyle(raw: string | null | undefined): LightStyle | undefined {
  if (!raw) return undefined;
  return LIGHT_STYLES.includes(raw as LightStyle) ? (raw as LightStyle) : undefined;
}

function pickColor(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : undefined;
}

function pickNumber(raw: string | null | undefined, min: number, max: number): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(max, Math.max(min, n));
}

function pickBool(raw: string | null | undefined): boolean | undefined {
  if (!raw) return undefined;
  if (raw === "1" || raw.toLowerCase() === "true") return true;
  if (raw === "0" || raw.toLowerCase() === "false") return false;
  return undefined;
}

function pickMode(raw: string | null | undefined): DesignMode | undefined {
  if (!raw) return undefined;
  return DESIGN_MODES.includes(raw as DesignMode) ? (raw as DesignMode) : undefined;
}

function pickRasterMode(raw: string | null | undefined): CpbCenterRgbRasterMode | undefined {
  if (!raw) return undefined;
  return RASTER_MODES.includes(raw as CpbCenterRgbRasterMode)
    ? (raw as CpbCenterRgbRasterMode)
    : undefined;
}

function toBool(v: unknown): boolean | undefined {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return pickBool(v);
  return undefined;
}

function toStringOrNull(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (typeof v === "string") return v;
  return undefined;
}

function resolveCenterPatch(
  center: Partial<CpbCenterDesignPayload> | undefined,
  qs: URLSearchParams | null,
): Partial<ConfiguratorState> {
  const mode = pickMode(qs?.get("centerMode") ?? center?.mode);
  const text = qs?.get("centerText") ?? (typeof center?.text === "string" ? center.text : undefined);
  const textColor = pickColor(qs?.get("centerTextColor") ?? center?.textColor);
  const textRgb = toBool(qs?.get("centerTextRgb") ?? center?.textRgb);
  const logoUrl = toStringOrNull(qs?.get("centerLogoUrl") ?? center?.logo?.url);
  const logoRgb = toBool(qs?.get("centerLogoRgb") ?? center?.logo?.rgb);
  const assetRevision = toStringOrNull(qs?.get("centerAssetRevision") ?? center?.assetRevision);
  const logoRasterMode = pickRasterMode(
    qs?.get("centerLogoRgbRasterMode") ?? center?.logo?.rgbRasterMode,
  );
  const patch: Partial<ConfiguratorState> = {};

  // Keep these reads explicit so the frozen payload fields are validated in one place.
  const _logoMimeType = toStringOrNull(center?.logo?.mimeType);
  const _assetRevision = assetRevision;
  const _logoRasterMode = logoRasterMode;
  void _logoMimeType;
  void _assetRevision;
  void _logoRasterMode;

  if (mode) patch.designMode = mode;
  if (text !== undefined) patch.centerText = sanitizeCenterTextInput(text);
  if (textColor) patch.textColor = textColor;
  if (textRgb !== undefined) patch.textRgb = textRgb;
  if (logoUrl !== undefined) patch.logoImageUrl = logoUrl;
  if (assetRevision !== undefined) patch.logoAssetRevision = assetRevision ?? "";
  if (logoRgb !== undefined) patch.logoRgb = logoRgb;
  return patch;
}

export function resolveInitialConfiguratorState(): ConfiguratorState {
  const base = { ...defaultConfiguratorState, logoTransform: { ...DEFAULT_LOGO_TRANSFORM } };
  const cpb = typeof window !== "undefined" ? window.__CPB_DEFAULTS__ : undefined;
  const cpbCenter = typeof window !== "undefined" ? window.__CPB_CENTER_DESIGN__ : undefined;
  const qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;

  const shape = pickShape(qs?.get("shape") ?? cpb?.shape);
  const frameColor = pickColor(qs?.get("frameColor") ?? cpb?.frameColor);
  const size = pickNumber(String(qs?.get("size") ?? cpb?.size ?? ""), 18, 72);
  const ledColor = pickColor(qs?.get("ledColor") ?? cpb?.ledColor);
  const lightStyle = pickLightStyle(qs?.get("lightStyle") ?? cpb?.lightStyle);
  const rgb = pickBool(qs?.get("rgb") ?? (cpb?.rgb === undefined ? undefined : String(cpb.rgb)));
  const depthLayers = pickNumber(
    String(qs?.get("depthLayers") ?? cpb?.depthLayers ?? ""),
    6,
    24,
  );
  const brightness = pickNumber(
    String(qs?.get("brightness") ?? cpb?.brightness ?? ""),
    0.2,
    2,
  );
  const ledTextFontRaw = qs?.get("ledTextFont")?.trim();
  const centerPatch = resolveCenterPatch(cpbCenter, qs);

  return {
    ...base,
    ...(shape ? { shape } : {}),
    ...(frameColor ? { frameColor } : {}),
    ...(size ? { size } : {}),
    ...(ledColor ? { ledColor } : {}),
    ...(lightStyle ? { lightStyle } : {}),
    ...(rgb === undefined ? {} : { rgb }),
    ...(depthLayers ? { depthLayers } : {}),
    ...(brightness ? { brightness } : {}),
    ...(ledTextFontRaw ? { ledTextFont: ledTextFontRaw } : {}),
    ...centerPatch,
  };
}
